import { useMemo, useState, useEffect } from 'react'
import { useParams, useNavigate } from 'react-router-dom'
import { useQuery } from '@tanstack/react-query'
import api from '@/lib/api'
import FundIdentityCard from '@/components/ui/FundIdentityCard'
import { Search } from 'lucide-react'

type Fund = { id: string; name: string; ticker: string; source: string }

/** Standalone fund profile — shows the Fund Identity Card (metadata +
 *  factsheet) without triggering any report engines. Useful for a quick
 *  look at a fund's static data.
 */
export default function FundIdentity() {
  const { fundId: urlFundId } = useParams<{ fundId?: string }>()
  const navigate = useNavigate()
  const [search, setSearch] = useState('')
  const [fundId, setFundId] = useState(urlFundId || '')

  // Keep URL + selection in sync (so /fund/bbg:48 auto-selects AQANEFI).
  useEffect(() => {
    if (urlFundId && urlFundId !== fundId) setFundId(urlFundId)
  }, [urlFundId])

  const fundsQ = useQuery<Fund[]>({
    queryKey: ['fa-funds'],
    queryFn: () => api.get('/fund-analysis/funds').then(r => r.data),
  })

  const filteredFunds = useMemo(() => {
    if (!fundsQ.data) return []
    const s = search.toLowerCase()
    return fundsQ.data
      .filter(f => !s ||
        f.name?.toLowerCase().includes(s) ||
        f.ticker?.toLowerCase().includes(s))
      .slice(0, 25)
  }, [fundsQ.data, search])

  const selectFund = (id: string) => {
    setFundId(id)
    navigate(`/fund/${encodeURIComponent(id)}`)
    setSearch('')
  }

  return (
    <div className="p-6 space-y-4 max-w-5xl">
      <div>
        <h1 className="text-2xl font-bold">Fund Identity</h1>
        <p className="text-muted-foreground text-sm">
          Static profile card — metadata, identifiers, factsheet.
          No analytics engines run; loads instantly.
        </p>
      </div>

      {/* Fund picker with autocomplete */}
      <div className="bg-card border border-border rounded-lg p-4 space-y-2">
        <div className="flex items-center gap-2">
          <Search className="h-4 w-4 text-muted-foreground" />
          <input
            type="text"
            placeholder="Search by fund name or ticker (e.g. AQANEFI, QQQ, Millennium)..."
            value={search}
            onChange={e => setSearch(e.target.value)}
            className="input flex-1"
          />
          {fundId && (
            <span className="text-xs text-muted-foreground font-mono">
              selected: {fundId}
            </span>
          )}
        </div>
        {search.length > 0 && filteredFunds.length > 0 && (
          <div className="border border-border rounded divide-y divide-border max-h-60 overflow-y-auto">
            {filteredFunds.map(f => (
              <button
                key={f.id}
                type="button"
                onClick={() => selectFund(f.id)}
                className="w-full text-left px-3 py-1.5 text-sm hover:bg-muted flex items-center gap-2"
              >
                <span className="font-medium truncate flex-1" title={f.name}>{f.name}</span>
                <span className="text-xs font-mono text-muted-foreground truncate max-w-[12rem]" title={f.ticker}>
                  {f.ticker}
                </span>
                <span className="text-[10px] font-semibold uppercase px-1.5 py-0.5 rounded bg-muted text-muted-foreground">
                  {f.source}
                </span>
              </button>
            ))}
          </div>
        )}
      </div>

      {/* The card itself */}
      {fundId ? (
        <FundIdentityCard fundId={fundId} />
      ) : (
        <div className="bg-card border border-border rounded-lg p-8 text-center text-muted-foreground text-sm">
          Pick a fund above to load its identity card.
        </div>
      )}
    </div>
  )
}
